import { useField, useFormikContext } from "formik"
import { ButtonBase, Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { LocalizedEnum, LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { DaysOfWeek, DaysOfWeekType } from "../../menuPlanningTypes"

const useStyles = makeStyles()((theme) => ({
    day: {
        width: "2rem",
        height: "2rem",
        borderRadius: "50%",
        fontSize: ".75rem",
        fontWeight: 700,
    },
    daySelected: {
        background: theme.palette.tailwind.slate[800],
        color: theme.palette.common.white,
    },
    dayUnselected: {
        background: theme.palette.common.white,
        color: theme.palette.tailwind.slate[800],
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
    },
    dayError: {
        border: `1px solid ${theme.palette.error.main}`,
    },
    hint: {
        color: theme.palette.tailwind.slate[500],
    },
    error: {
        color: theme.palette.error.main,
    },
}))

// The id `StandardFormikErrorScrollWrapper` scrolls to on submit, and the name Yup's error path resolves to.
const FIELD_NAME = "availability.weekDays"

/** Round weekday toggles, shown directly in the dish form rather than as a configurable field card. */
export function DishAvailabilityField() {
    const { classes, cx } = useStyles()
    const [field, meta, helpers] = useField<DaysOfWeekType[]>(FIELD_NAME)
    const { submitCount } = useFormikContext()

    const hasError = Boolean(meta.error) && (submitCount > 0 || meta.touched)

    const toggleDay = (day: DaysOfWeekType) =>
        helpers.setValue(
            field.value.includes(day) ? field.value.filter((selected) => selected !== day) : [...field.value, day],
        )

    return (
        <Stack id={FIELD_NAME} spacing=".5rem">
            <LocalizedStrict id="merchant-menu-templates-field-availability">
                <Typography variant="subtitle1">Availability</Typography>
            </LocalizedStrict>
            <Stack direction="row" spacing=".5rem" flexWrap="wrap">
                {Object.values(DaysOfWeek).map((day) => (
                    <LocalizedEnum key={day} base="merchant-menu-templates-week-day-short" enumValue={day}>
                        <ButtonBase
                            className={cx(
                                classes.day,
                                field.value.includes(day) ? classes.daySelected : classes.dayUnselected,
                                hasError && classes.dayError,
                            )}
                            onClick={() => toggleDay(day)}
                        >
                            {day.slice(0, 2)}
                        </ButtonBase>
                    </LocalizedEnum>
                ))}
            </Stack>
            <LocalizedStrict id="merchant-menu-templates-availability-hint">
                <Typography variant="caption" className={classes.hint}>
                    Which weekdays this dish appears on in the week menu.
                </Typography>
            </LocalizedStrict>
            {hasError && (
                <Typography variant="caption" className={classes.error}>
                    {meta.error}
                </Typography>
            )}
        </Stack>
    )
}
